import { Stack, useMediaQuery, useTheme } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";

import { useHasExamPacsAction } from "../../components/Exams/ExamPacsAction/useHasExamPacsAction";
import NoData from "../../components/UI/NoData";
import PagesContainer from "../../components/UI/PagesContainer";
import useFetch from "../../hooks/useFetch";
import { usePage } from "../../hooks/usePage";
import { IExams } from "../../models/exams";
import { getManagementExams } from "../../services/exams.service";
import { tokenService } from "../../services/token.service";
import { useAppSelector } from "../../store/store";
import ExamsList from "../Exams/ExamsList";
import ManagementLaboratorialExamsFilter from "./ManagementLaboratorialExamsFilter";
import ManagementLaboratorialExamsRowActions from "./ManagementLaboratorialExamsRowActions";
import ManagementLaboratorialExamsTable from "./ManagementLaboratorialExamsTable";

export interface IManagementLaboratorialExamsFilter {
  StartDate: Dayjs | null;
  EndDate: Dayjs | null;
  ExamType: string;
  IndividualId?: string;
}
const INTERNAL_PHYSICIAN_PROFILE_CODE = "9";
const EXTERNAL_PHYSICIAN_PROFILE_CODE = "10";
const EXAM_MANAGER_PROFILE_CODE = "11";

const ManagementLaboratorialExamsPage = () => {
  const { pageTitle } = usePage();
  const { individual } = useAppSelector((state) => state.user);

  const hasExamManagerProfile = individual.profiles.some(
    (profile) => profile.code === EXAM_MANAGER_PROFILE_CODE || profile.code === INTERNAL_PHYSICIAN_PROFILE_CODE || profile.code === EXTERNAL_PHYSICIAN_PROFILE_CODE,
  );

  const [exams, setExams] = useState<IExams[]>([]);
  const [rowCount, setRowCount] = useState<number>(0);
  const [pageCount, setPageCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });

  const {
    hasPixeonExtension,
    hasVueMotionExtension,
    hasExamPacsAction,
    loadingPacsValues,
    pacsColumnName,
    handleFormatExams,
  } = useHasExamPacsAction();

  const formattedExams = useMemo<IExams[]>(() => {
    const examsUpdate = handleFormatExams(exams);
    return examsUpdate as IExams[];
  }, [handleFormatExams, exams]);

  const { sendRequest: getExamsRequest, loading } =
    useFetch(getManagementExams);

  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down("lg"));

  const [filter, setFilter] = useState<IManagementLaboratorialExamsFilter>({
    ExamType: "all",
    StartDate: dayjs(),
    EndDate: dayjs(),
  });

  const handlePageChange = (page: number) => {
    setPaginationModel((prev) => ({ ...prev, page: page - 1 }));
  };

  const fetch = useCallback(async () => {
    if (!filter) return;

    const { data, success } = await getExamsRequest({
      StartDate:
        filter.StartDate && filter.StartDate.isValid()
          ? filter.StartDate.toISOString().substring(0, 10)
          : undefined,
      EndDate:
        filter.EndDate && filter.EndDate.isValid()
          ? filter.EndDate.toISOString().substring(0, 10)
          : undefined,
      ExamType: filter.ExamType === "all" ? undefined : filter.ExamType,
      IndividualId: filter.IndividualId,
      PageNumber: paginationModel.page + 1,
      PageSize: paginationModel.pageSize,
    });

    if (success && data) {
      setExams(data.items);
      setPageCount(data.totalPages);
      setRowCount(data.totalCount);
    } else {
      setExams([]);
      setPageCount(0);
      setRowCount(0);
    }
  }, [getExamsRequest, filter, paginationModel]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  if (!tokenService.hasAccessTokens()) {
    return <Navigate to="/login" />;
  }

  if (!hasExamManagerProfile) {
    return <Navigate to="/no-access" />;
  }

  return (
    <PagesContainer height={"100%"} title={pageTitle || "Gestao de exames"}>
      <ManagementLaboratorialExamsFilter
        filter={filter}
        onFilterChange={setFilter}
      />
      {!matches && (
        <ManagementLaboratorialExamsTable
          data={formattedExams}
          individualId={filter.IndividualId}
          hasPixeonExtension={hasPixeonExtension}
          hasVueMotionExtension={hasVueMotionExtension}
          hasExamPacsAction={hasExamPacsAction}
          loadingPacsValues={loadingPacsValues}
          pacsColumnName={pacsColumnName}
          onEmptyReleaseDateSuccess={fetch}
          tableProps={{
            onPaginationModelChange: setPaginationModel,
            paginationModel,
            rowCount,
            loading,
            slots: {
              noRowsOverlay: () => (
                <Stack height={"100%"} justifyContent={"center"}>
                  <NoData />
                </Stack>
              ),
            },
          }}
        />
      )}
      {matches && (
        <ExamsList
          data={formattedExams}
          hasPixeonExtension={hasPixeonExtension}
          hasVueMotionExtension={hasVueMotionExtension}
          hasExamPacsAction={hasExamPacsAction}
          loadingPacsValues={loadingPacsValues}
          pacsColumnName={pacsColumnName}
          page={paginationModel.page + 1}
          onPageChange={handlePageChange}
          count={pageCount}
          individualId={filter.IndividualId}
          renderActions={(exam) => (
            <ManagementLaboratorialExamsRowActions
              exam={exam}
              onEmptyReleaseDateSuccess={fetch}
            />
          )}
        />
      )}
    </PagesContainer>
  );
};

export default ManagementLaboratorialExamsPage;
